import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { AppConfigService } from '~/core/services/appconfig.service';

/**
 * Attaches the MSAL ID token as a Bearer token to every backend API request.
 * The ID token's audience is this app's clientId, which the backend validates.
 */
@Injectable()
export class ApiAuthInterceptor implements HttpInterceptor {
    constructor(
        private msalService: MsalService,
        private config: AppConfigService
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const apiUrl: string = this.config.getConfig('apiUrl');

        // Only attach the token to calls targeting the backend API.
        if (!apiUrl || !req.url.startsWith(apiUrl)) {
            return next.handle(req);
        }

        const account =
            this.msalService.instance.getActiveAccount() ??
            this.msalService.instance.getAllAccounts()[0];

        if (!account) {
            return next.handle(req);
        }

        return from(
            this.msalService.instance.acquireTokenSilent({
                account,
                scopes: ['openid', 'profile', 'email']
            })
        ).pipe(
            switchMap((result) => {
                const authReq = req.clone({
                    setHeaders: { Authorization: `Bearer ${result.idToken}` }
                });
                return next.handle(authReq);
            })
        );
    }
}
